import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormState } from 'react-hook-form';
import { z } from 'zod';
import { CreateReplyTemplateRequest, interpolateTemplateBody, TEMPLATE_TOKENS, } from '@wit-support/admin-contracts';
import { composeOutbound } from '@wit-support/email-render';
import { interpolateDataTokens, validateTemplateDataTokens, } from '@wit-support/data-providers';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, FormField, HtmlEditor, HtmlPreview, Label, Select, toast, } from '@wit-support/ui';
import { Actions, SubmitButton, ZodField, ZodForm, useZodFormContext } from '@wit-support/ui-forms';
import { createTemplate, fetchEmailLayout, fetchTemplateDataProviders, updateTemplate, } from '../api.js';
import { stripTags } from '../lib.js';
import { TokenList } from '../components/token-list.js';
const PREVIEW_CTX = {
    contact: { name: 'Sample Customer', email: 'sample@example.com' },
    agent: { name: 'WIT Support' },
    conversation: {
        id: '00000000-0000-0000-0000-000000000000',
        number: 100042,
        subject: 'Your subscription',
    },
};
/**
 * The catalog endpoint ships `{ path, label, type }` WITHOUT a sample, so the
 * editor preview derives an obviously-placeholder value per field. URL-shaped
 * fields get a sample https link so links/buttons render a real-looking target;
 * everything else uses an angle-bracketed label. The real values are fetched at
 * send time by the provider gate.
 */
const looksLikeUrl = (path) => /url|link|href/i.test(path);
const sampleValueForField = (field) => looksLikeUrl(field.path) ? 'https://example.com/login?token=demo' : `‹${field.label}›`;
const buildSampleValues = (provider) => {
    if (!provider)
        return {};
    const values = {};
    for (const field of provider.fields)
        values[field.path] = sampleValueForField(field);
    return values;
};
// Local form schema: `name`/`body` are picked from the canonical contract (same
// min/max + .meta() label/placeholder → identical error text), `subject` is a
// plain (always-a-string) field so the controlled editor never sees `null`,
// and `dataProviderKey` mirrors the contract's nullable string. The empty-HTML
// guard mirrors the previous hand-rolled `validate()` — Zod's min(1) on body
// passes visually-empty markup like `<p></p>`, so this blocks submit for that
// case explicitly.
const TemplateFormSchema = CreateReplyTemplateRequest.pick({ name: true, body: true })
    .extend({
    subject: z.string(),
    dataProviderKey: z.string().nullable(),
})
    .superRefine((v, ctx) => {
    if (stripTags(v.body).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['body'],
            message: 'Body cannot be empty',
        });
    }
});
function TemplateFormFields({ isEdit, pending, onCancel }) {
    const { form } = useZodFormContext();
    const body = form.watch('body');
    const subject = form.watch('subject');
    const dataProviderKey = form.watch('dataProviderKey');
    // react-hook-form's `formState` is a Proxy that only marks a property as
    // "watched" once something actually reads it during a render — reading
    // `form.formState.isSubmitted` as a plain property access (not through this
    // hook) never registers as a read, so a Controller whose render never
    // touches it otherwise would not reliably reflect it. `useFormState` here
    // reads (and so properly tracks) `isSubmitted` the same way FieldShell does
    // for every default-widget field.
    const { isSubmitted } = useFormState({ control: form.control });
    // Static provider catalog ("data action" binding). Long stale time — this is a
    // shipped-static list today (the seam that becomes a live fetch later).
    const providersQuery = useQuery({
        queryKey: ['template-data-providers'],
        queryFn: fetchTemplateDataProviders,
        staleTime: 5 * 60_000,
    });
    const providers = providersQuery.data ?? [];
    const selectedProvider = providers.find((p) => p.key === dataProviderKey);
    // Data-token validation mirrors the server-side rule (unknown field, token
    // without/mismatched provider). Shown inline + blocks submit, same as the
    // name/body field errors.
    // Gate on `isSuccess`: until the catalog actually loads, `providers` is `[]`,
    // so every data token would spuriously report `unknown_field` (and disable
    // Save) during the brief load — or permanently if the endpoint is down. The
    // server re-validates on save, so deferring is safe.
    const dataTokenErrors = useMemo(() => providersQuery.isSuccess
        ? validateTemplateDataTokens(body, subject.trim() || null, dataProviderKey, providers)
        : [], [providersQuery.isSuccess, body, subject, dataProviderKey, providers]);
    // Data-token errors additionally disable the submit button, per the spec —
    // they're always visible inline, so a disabled button is unambiguous.
    const hasDataTokenErrors = dataTokenErrors.length > 0;
    // Compose the preview against the real default layout — that's what the
    // recipient will actually see. Templates store INNER content only; the
    // layout owns header + footer + signature. Before this fix the preview
    // rendered raw inner HTML and agents thought the brand styles weren't
    // applied.
    const layoutQuery = useQuery({
        queryKey: ['email-layout'],
        queryFn: fetchEmailLayout,
        staleTime: 5 * 60_000,
    });
    const previewSubject = subject ? interpolateTemplateBody(subject, PREVIEW_CTX) : '';
    const previewHtml = useMemo(() => {
        let interpolatedBody = interpolateTemplateBody(body, PREVIEW_CTX, { escapeHtml: true });
        // Substitute data tokens with obvious sample values so links/buttons render a
        // realistic target in the preview. Real values are fetched at send time.
        if (dataProviderKey) {
            interpolatedBody = interpolateDataTokens(interpolatedBody, dataProviderKey, buildSampleValues(selectedProvider));
        }
        if (!layoutQuery.data)
            return interpolatedBody; // loading
        return composeOutbound({
            layout: { html: layoutQuery.data.html },
            agent: { displayName: PREVIEW_CTX.agent.name, signatureHtml: null },
            contact: {
                displayName: PREVIEW_CTX.contact.name,
                email: PREVIEW_CTX.contact.email,
            },
            subject: previewSubject || 'Your subscription',
            bodyHtml: interpolatedBody,
        }).html;
    }, [body, dataProviderKey, selectedProvider, layoutQuery.data, previewSubject]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid min-h-0 flex-1 gap-6 overflow-auto grid-cols-[repeat(auto-fit,minmax(420px,1fr))]", children: [_jsxs("div", { className: "flex flex-col gap-4", children: [_jsx(ZodField, { name: "name", autoFocus: true }), _jsx(ZodField, { name: "subject", label: "Subject", placeholder: "e.g. Subscription cancelled", description: "Optional \u2014 replaces the conversation subject when the agent inserts this template." }), _jsx(ZodField, { name: "body", render: ({ field, fieldState }) => {
                                    // Read `fieldState.error` unconditionally (not behind the
                                    // `showErr` short-circuit below) — react-hook-form's formState
                                    // is a Proxy that only tracks a property as "watched" once it's
                                    // actually read during a render. A conditional read that never
                                    // touches `.error` on the (pre-submit) mount render means this
                                    // Controller is never notified when the error later appears, so
                                    // it silently never re-renders — a field with no autofocus/blur
                                    // interaction would then block submit with NO visible reason.
                                    const err = fieldState.error;
                                    const showErr = (fieldState.isTouched || isSubmitted) && err;
                                    return (_jsxs(FormField, { children: [_jsx(FormField.Label, { htmlFor: "template-body", children: "Body" }), _jsx(HtmlEditor, { id: "template-body", value: field.value ?? '', onChange: field.onChange, placeholder: '<div style="max-width:600px;margin:0 auto;...">...</div>', maxHeight: "600px", "data-testid": "template-body", "aria-invalid": showErr ? true : undefined, "aria-describedby": showErr ? 'template-body-err' : 'template-body-help' }), showErr ? (_jsx(FormField.Error, { id: "template-body-err", children: err.message })) : null, _jsxs("div", { id: "template-body-help", className: "mt-1", children: [_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "Available tokens:" }), _jsx(TokenList, { items: TEMPLATE_TOKENS.map((t) => ({
                                                            token: t.token,
                                                            description: t.description,
                                                        })) })] })] }));
                                } }), _jsx(ZodField, { name: "dataProviderKey", render: ({ field }) => (_jsxs(FormField, { children: [_jsx(FormField.Label, { htmlFor: "template-data-provider", children: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u0435 \u043F\u0435\u0440\u0435\u0434 \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u043E\u0439" }), _jsxs(Select, { value: field.value ?? '__none__', onValueChange: (v) => field.onChange(v === '__none__' ? null : v), children: [_jsx(Select.Trigger, { id: "template-data-provider", "aria-label": "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u0435 \u043F\u0435\u0440\u0435\u0434 \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u043E\u0439", children: _jsx(Select.Value, { placeholder: "(\u043D\u0435\u0442)" }) }), _jsxs(Select.Content, { children: [_jsx(Select.Item, { value: "__none__", children: "(\u043D\u0435\u0442)" }), providers.map((p) => (_jsx(Select.Item, { value: p.key, children: p.label }, p.key)))] })] }), _jsx(FormField.Description, { children: "\u041F\u0440\u043E\u0432\u0430\u0439\u0434\u0435\u0440 \u0437\u0430\u043F\u0443\u0441\u043A\u0430\u0435\u0442\u0441\u044F \u043F\u0435\u0440\u0435\u0434 \u043A\u0430\u0436\u0434\u043E\u0439 \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u043E\u0439 \u0438 \u043F\u043E\u0434\u0441\u0442\u0430\u0432\u043B\u044F\u0435\u0442 \u0432\u043D\u0435\u0448\u043D\u0438\u0435 \u0434\u0430\u043D\u043D\u044B\u0435." }), dataProviderKey && selectedProvider ? (_jsxs("div", { className: "mt-1", children: [_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0414\u043E\u0441\u0442\u0443\u043F\u043D\u044B\u0435 \u043F\u0435\u0440\u0435\u043C\u0435\u043D\u043D\u044B\u0435:" }), _jsx(TokenList, { items: selectedProvider.fields.map((f) => {
                                                        const token = `{{data.${dataProviderKey}.${f.path}}}`;
                                                        return {
                                                            token,
                                                            description: f.label,
                                                            onInsert: () => form.setValue('body', `${body}${token}`, {
                                                                shouldDirty: true,
                                                                shouldValidate: isSubmitted,
                                                            }),
                                                        };
                                                    }) })] })) : null, dataTokenErrors.length > 0 ? (_jsx("ul", { className: "mt-1 flex flex-col gap-0.5", children: dataTokenErrors.map((e, i) => (_jsx("li", { className: "text-xs text-[var(--color-state-danger-fg)]", children: e.code === 'provider_unbound'
                                                    ? `Переменная {{data.${e.provider}.${e.field}}} используется, но действие не выбрано`
                                                    : e.code === 'provider_mismatch'
                                                        ? `Переменная {{data.${e.provider}.${e.field}}} не относится к выбранному действию`
                                                        : `Неизвестная переменная {{data.${e.provider}.${e.field}}}` }, `${e.code}-${e.provider ?? ''}-${e.field ?? ''}-${i}`))) })) : null] })) })] }), _jsxs("div", { className: "flex min-h-0 flex-col gap-1.5", children: [_jsx(Label, { children: "Live preview" }), _jsxs("div", { className: "rounded-[var(--radius-2)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] px-3 py-2 text-xs", children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: "Subject: " }), _jsx("span", { children: previewSubject || '(from conversation)' })] }), _jsx("div", { className: "min-h-0 flex-1", children: _jsx(HtmlPreview, { html: previewHtml, height: "100%" }) }), dataProviderKey ? (_jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "\u0420\u0435\u0430\u043B\u044C\u043D\u044B\u0435 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u043F\u043E\u0434\u0441\u0442\u0430\u0432\u043B\u044F\u044E\u0442\u0441\u044F \u043F\u0440\u0438 \u043E\u0442\u043F\u0440\u0430\u0432\u043A\u0435." })) : null] })] }), _jsxs(Actions, { className: "pt-0", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: onCancel, disabled: pending, children: "Cancel" }), _jsx(SubmitButton, { disabled: pending || hasDataTokenErrors, pendingLabel: "Saving\u2026", children: isEdit ? 'Save' : 'Create' })] })] }));
}
export function TemplateFormDialog({ open, onOpenChange, template, onSaved, }) {
    const isEdit = template !== undefined;
    const qc = useQueryClient();
    const createMut = useMutation({
        mutationFn: (input) => createTemplate(input),
        onSuccess: (t) => {
            toast.success('Template created');
            void qc.invalidateQueries({ queryKey: ['templates'] });
            onSaved?.(t);
            onOpenChange(false);
        },
        onError: (err) => toast.error(err.message),
    });
    const updateMut = useMutation({
        mutationFn: (input) => updateTemplate(template.id, input),
        onSuccess: (t) => {
            toast.success('Template updated');
            void qc.invalidateQueries({ queryKey: ['templates'] });
            onSaved?.(t);
            onOpenChange(false);
        },
        onError: (err) => toast.error(err.message),
    });
    const pending = createMut.isPending || updateMut.isPending;
    function handleSave(v) {
        // Defense-in-depth: the submit button is already disabled while data-token
        // errors are present, but a keyboard Enter-submit doesn't always respect a
        // disabled submit button, so re-check against the cached catalog here too.
        // Only recheck when the catalog has actually loaded (cache hit): an empty
        // or missing cache must NOT be treated as "provider catalog is empty" —
        // that would flag every bound {{data.*}} token as unknown_field and
        // silently swallow the save with no feedback. If the catalog genuinely
        // isn't loaded yet, skip this client-side recheck and let the server's
        // own validation (which always runs) surface any real error via toast.
        const providers = qc.getQueryData(['template-data-providers']);
        const normalizedSubject = v.subject.trim() || null;
        if (providers) {
            const dataTokenErrors = validateTemplateDataTokens(v.body, normalizedSubject, v.dataProviderKey, providers);
            if (dataTokenErrors.length > 0) {
                toast.error(dataTokenErrors
                    .map((e) => e.code === 'provider_unbound'
                    ? `Переменная {{data.${e.provider}.${e.field}}} используется, но действие не выбрано`
                    : e.code === 'provider_mismatch'
                        ? `Переменная {{data.${e.provider}.${e.field}}} не относится к выбранному действию`
                        : `Неизвестная переменная {{data.${e.provider}.${e.field}}}`)
                    .join('; '));
                return;
            }
        }
        if (isEdit) {
            const payload = {};
            if (v.name !== template.name)
                payload.name = v.name;
            if (normalizedSubject !== template.subject)
                payload.subject = normalizedSubject;
            if (v.body !== template.body)
                payload.body = v.body;
            if (v.dataProviderKey !== (template.dataProviderKey ?? null)) {
                payload.dataProviderKey = v.dataProviderKey;
            }
            if (Object.keys(payload).length === 0) {
                onOpenChange(false);
                return;
            }
            updateMut.mutate(payload);
        }
        else {
            createMut.mutate({
                name: v.name,
                subject: normalizedSubject,
                body: v.body,
                dataProviderKey: v.dataProviderKey,
            });
        }
    }
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsxs(DialogContent, { size: "page", children: [_jsxs(DialogHeader, { className: "gap-4", children: [_jsx(DialogTitle, { children: isEdit ? 'Edit template' : 'New template' }), _jsx(DialogDescription, { children: "Templates are team-wide HTML emails. Edit the raw HTML on the left; the live preview on the right shows exactly how the recipient will see it." })] }), open ? (_jsx(ZodForm, { schema: TemplateFormSchema, defaultValues: {
                        name: template?.name ?? '',
                        subject: template?.subject ?? '',
                        body: template?.body ?? '',
                        dataProviderKey: template?.dataProviderKey ?? null,
                    }, className: "flex min-h-0 flex-1 flex-col gap-4 overflow-hidden", onSubmit: (v) => handleSave(v), children: _jsx(TemplateFormFields, { isEdit: isEdit, pending: pending, onCancel: () => onOpenChange(false) }) }, template?.id ?? 'new')) : null] }) }));
}
//# sourceMappingURL=create-dialog.js.map